import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramVelocityOfAir } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import {
  calcAirDensityLbFt3FromVelocityAndPressure,
  calcVelocityFpmFromVelocityPressure,
  calcVelocityPressureInWgFromVelocity,
  STANDARD_AIR_DENSITY_LB_FT3,
} from '../../../lib/calcs';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.velocityOfAir.v1';

type Fields = { velocityPressure: string; velocity: string; density: string };
const EMPTY: Fields = { velocityPressure: '', velocity: '', density: '' };

/**
 * Velocity of Air — V = 4005 × √(Pv × (0.075 / ρ))
 * Standard air density 0.075 lb/ft³ is assumed when the density field is blank.
 * Internal math always in Imperial.
 */
const AirflowVelocityOfAirScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  const calculate = useCallback(() => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const standardDensity = STANDARD_AIR_DENSITY_LB_FT3;
    // Pressure and velocity always converted to Imperial for the formula
    const pv = toImp(fields.velocityPressure, 'pressure');
    const v = toImp(fields.velocity, 'velocity');
    // Density is always lb/ft³ — density qty has its own conversion
    const rho = toImp(fields.density, 'density');

    if (fields.velocity === '') {
      if (fields.density !== '' && rho <= 0) {
        setError('Air density must be greater than zero.');
        return;
      }
      if (pv < 0) {
        setError('Velocity pressure cannot be negative.');
        return;
      }
      const useRho = fields.density !== '' ? rho : standardDensity;
      const computed = calcVelocityFpmFromVelocityPressure(pv, useRho);
      if (!Number.isFinite(computed)) {
        setError('Unable to compute velocity from the given inputs.');
        return;
      }
      setSolvedFor('velocity');
      if (fields.density === '') {
        set({ velocity: fromImp(computed, 'velocity', 1), density: fromImp(standardDensity, 'density', 4) });
      } else {
        set({ velocity: fromImp(computed, 'velocity', 1) });
      }
    } else if (fields.velocityPressure === '') {
      if (fields.density !== '' && rho <= 0) {
        setError('Air density must be greater than zero.');
        return;
      }
      if (v <= 0) {
        setError('Velocity must be greater than zero.');
        return;
      }
      const useRho = fields.density !== '' ? rho : standardDensity;
      const computed = calcVelocityPressureInWgFromVelocity(v, useRho);
      if (!Number.isFinite(computed)) {
        setError('Unable to compute velocity pressure from the given inputs.');
        return;
      }
      setSolvedFor('velocityPressure');
      if (fields.density === '') {
        set({ velocityPressure: fromImp(computed, 'pressure', 4), density: fromImp(standardDensity, 'density', 4) });
      } else {
        set({ velocityPressure: fromImp(computed, 'pressure', 4) });
      }
    } else if (fields.density === '') {
      if (v <= 0) {
        setError('Velocity must be greater than zero.');
        return;
      }
      if (pv < 0) {
        setError('Velocity pressure cannot be negative.');
        return;
      }
      const computed = calcAirDensityLbFt3FromVelocityAndPressure(v, pv);
      if (!Number.isFinite(computed)) {
        setError('Unable to compute air density from the given inputs.');
        return;
      }
      setSolvedFor('density');
      set({ density: fromImp(computed, 'density', 4) });
    }
  }, [fields, toImp, fromImp, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  const result = useMemo(() => [
    fields.velocityPressure && { label: 'Velocity Pressure', value: fields.velocityPressure, unit: unit('pressure'), emphasis: solvedFor === 'velocityPressure' },
    fields.velocity && { label: 'Velocity', value: fields.velocity, unit: unit('velocity'), emphasis: solvedFor === 'velocity' },
    fields.density && { label: 'Air Density', value: fields.density, unit: unit('density'), emphasis: solvedFor === 'density' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[], [fields, unit, solvedFor]);

  return (
    <CalcShell
      title="Velocity of Air"
      subtitle="Calculate air velocity from velocity pressure and air density"
      formula={
        <>
          <FormulaLine>V = 4005 × √(Pv × (0.075 / ρ))</FormulaLine>
          <FormulaNote>
            V = Velocity ({unit('velocity')}), Pv = Velocity Pressure ({unit('pressure')}), ρ = Air Density ({unit('density')}).
            Standard air at 70°F: ρ = {isImperial ? '0.075 lb/ft³' : '1.2 kg/m³'}.
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: V = 4005 × √(Pv × (0.075 / ρ))"
      diagram={<DiagramVelocityOfAir />}
      outputSlots={{
        velocity_fpm: fields.velocity ? toImp(fields.velocity, 'velocity') : undefined,
        velocity_pressure_iwg: fields.velocityPressure
          ? toImp(fields.velocityPressure, 'pressure')
          : undefined,
        air_density_lb_ft3: fields.density ? toImp(fields.density, 'density') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.velocity_fpm != null && !fields.velocity) {
          patch.velocity = fromImp(values.velocity_fpm, 'velocity', 1);
        }
        if (values.velocity_pressure_iwg != null && !fields.velocityPressure) {
          patch.velocityPressure = fromImp(values.velocity_pressure_iwg, 'pressure', 4);
        }
        if (values.air_density_lb_ft3 != null && !fields.density) {
          patch.density = fromImp(values.air_density_lb_ft3, 'density', 4);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Velocity Pressure (${unit('pressure')})`}
          value={fields.velocityPressure}
          onChangeText={(v) => set({ velocityPressure: v })}
          rightText={unit('pressure')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Velocity (${unit('velocity')})`}
          value={fields.velocity}
          onChangeText={(v) => set({ velocity: v })}
          rightText={unit('velocity')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Air Density (${unit('density')})`}
          value={fields.density}
          onChangeText={(v) => set({ density: v })}
          rightText={unit('density')}
          placeholder={isImperial ? '0.075 for standard air' : '1.2 for standard air'}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirflowVelocityOfAirScreen;
